import React, { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTutorialStore } from '@/components/tutorials/useTutorialStore';

const TRIGGER = '[data-tutorial="account-menu"]';
const CONTENT = '[data-tutorial="account-menu-content"]';
const WRAPPER = 'account-menu-combined-highlight';

function measureUnion(triggerSel: string, contentSel: string): DOMRect | null {
  const trigger = document.querySelector(triggerSel);
  const content = document.querySelector(contentSel);
  if (!trigger || !content) return null;
  const r1 = trigger.getBoundingClientRect();
  const r2 = content.getBoundingClientRect();
  const left = Math.min(r1.left, r2.left);
  const top = Math.min(r1.top, r2.top);
  const right = Math.max(r1.right, r2.right);
  const bottom = Math.max(r1.bottom, r2.bottom);
  return new DOMRect(left, top, right - left, bottom - top);
}

/** Union highlight for Settings gear + open dropdown during guided filter post-apply step 2 (Resources). */
export const GuidedFilterPostApplyCombinedHighlight: React.FC = () => {
  const { guidedFilterPostApplyActive, guidedFilterPostApplyStep } = useTutorialStore();
  const active = guidedFilterPostApplyActive && guidedFilterPostApplyStep === 2;
  const [union, setUnion] = useState<DOMRect | null>(null);

  useLayoutEffect(() => {
    if (!active) {
      setUnion(null);
      return;
    }

    const update = () => setUnion(measureUnion(TRIGGER, CONTENT));
    update();
    const t1 = setTimeout(update, 50);
    const t2 = setTimeout(update, 150);
    const resize = () => update();
    window.addEventListener('resize', resize);
    window.addEventListener('scroll', resize, true);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', resize);
      window.removeEventListener('scroll', resize, true);
    };
  }, [active]);

  if (!active || !union || typeof document === 'undefined') return null;

  const el = (
    <div
      data-tutorial={WRAPPER}
      aria-hidden
      style={{
        position: 'fixed',
        left: union.left,
        top: union.top,
        width: union.width,
        height: union.height,
        pointerEvents: 'none',
        zIndex: 9998,
      }}
    />
  );

  return createPortal(el, document.body);
};
